import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  TemplateRef,
  computed,
  contentChildren,
  effect,
  inject,
  input,
  model,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { TabsModule } from 'primeng/tabs';
import { NotePanelDirective } from './note-panel.directive';
import { NoteTab, noteTabLabel } from './note.types';

/**
 * Reusable note page shell: projected header, a top tab strip, and one scrollable content area.
 *
 * Layout: header and tabs take their natural height; the content area is `flex-1 min-h-0
 * overflow-y-auto`, so the tabs stay reachable while long content scrolls. The host fills its parent
 * (`h-full`), so the parent decides the available height (the View route uses `h-dvh`, which follows
 * mobile browser chrome and orientation changes without any hard-coded dimensions).
 *
 * Tabs use PrimeNG Tabs for the WAI-ARIA tab pattern (roles, aria-selected/controls/labelledby,
 * roving tabindex, arrow/Home/End keys). `scrollable` gives a touch-scrollable strip on narrow
 * screens instead of squeezing labels.
 *
 * Panels are supplied as `<ng-template notePanel="id">`. A panel renders on its first visit and
 * then stays alive (hidden), so switching back is instant, no work is repeated and component state
 * survives. The scroll position of every tab is remembered while the same note stays open.
 *
 * Nothing here knows about View vs Edit, so View and Edit both use it unchanged.
 */
@Component({
  selector: 'app-note-shell',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, NgTemplateOutlet, TabsModule],
  host: { class: 'flex h-full min-h-0 w-full flex-col bg-surface-50 dark:bg-surface-950' },
  template: `
    <div class="shrink-0" [ngClass]="{ '!hidden': focusMode() }">
      <ng-content select="[noteHeader]" />
    </div>

    <p-tabs
      class="flex min-h-0 flex-1 flex-col [--p-tabs-tab-padding:0.34rem_0.56rem]"
      [value]="activeTab()"
      (valueChange)="onTabChange($event)"
      [scrollable]="true"
      [showNavigators]="false">
      <p-tablist class="shrink-0 bg-surface-0 dark:bg-surface-900" [ngClass]="{ '!hidden': focusMode() }">
        @for (tab of tabs(); track tab.id) {
          <p-tab [value]="tab.id" class="whitespace-nowrap">{{ labelOf(tab) }}</p-tab>
        }
      </p-tablist>

      <!-- [--edit-appbar-height:0px]: the rich editor's sticky toolbar offsets itself below this variable
           (originally the removed legacy Edit page's fixed appbar height, ~3.25rem). Here the header/tabs above already
           sit outside this scroll container, so the toolbar's sticky context starts right below them —
           zeroing the variable makes it stick flush under the tab bar instead of leaving a gap. -->
      <div #scroller class="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)] [--edit-appbar-height:0px]">
        <p-tabpanels class="!bg-transparent !p-0">
          @for (tab of tabs(); track tab.id) {
            <p-tabpanel [value]="tab.id">
              @if (visited().has(tab.id)) {
                <ng-container [ngTemplateOutlet]="templateFor(tab.id)" />
              }
            </p-tabpanel>
          }
        </p-tabpanels>
      </div>
    </p-tabs>

    <!-- Optional fixed bar under the content (used by Edit for save/sync status and actions). -->
    <div class="shrink-0 empty:hidden">
      <ng-content select="[noteFooter]" />
    </div>
  `,
})
export class NoteShellComponent {
  readonly tabs = input.required<NoteTab[]>();
  /** Two-way: `[(activeTab)]`. */
  readonly activeTab = model.required<string>();
  /** Panels rendered from the start (hidden until visited) instead of on first visit, for content
   * whose component must exist even if the user never opens the tab (e.g. Edit's attachment uploader). */
  readonly eagerTabs = input<readonly string[]>([]);
  /** Mobile-only Focus Mode (e.g. View's reading mode): hides the projected header and the tab strip
   * so the active panel's content fills the host, without navigating away or recreating the panel. */
  readonly focusMode = input<boolean>(false);

  private readonly panels = contentChildren(NotePanelDirective);
  // Optional: the first effect run can precede the first render of the view.
  private readonly scroller = viewChild<ElementRef<HTMLElement>>('scroller');
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Tabs whose panel has been rendered at least once (lazy first render, then kept alive). */
  protected readonly visited = signal<ReadonlySet<string>>(new Set<string>());
  private readonly templates = computed(() => new Map(this.panels().map((p) => [p.id(), p.template])));

  private readonly scrollPositions = new Map<string, number>();
  private shownTab: string | null = null;

  constructor() {
    effect(() => {
      const next = this.activeTab();
      untracked(() => this.showTab(next));
    });
    effect(() => {
      const eager = this.eagerTabs();
      untracked(() => {
        const missing = eager.filter((id) => !this.visited().has(id));
        if (missing.length) this.visited.update((set) => new Set([...set, ...missing]));
      });
    });
  }

  /** The active panel's scroll offset, for callers (e.g. Focus Mode) that hide/show in-panel content
   * above the reading position and need to compensate so the visible content doesn't jump. */
  get scrollTop(): number {
    return this.scroller()?.nativeElement.scrollTop ?? 0;
  }
  set scrollTop(value: number) {
    const el = this.scroller()?.nativeElement;
    if (el) el.scrollTop = value;
  }

  /** The scrolling content area (for callers that keep a reading position, e.g. View ⇄ Edit). */
  get scrollElement(): HTMLElement | null {
    return this.scroller()?.nativeElement ?? null;
  }

  protected labelOf(tab: NoteTab): string {
    return noteTabLabel(tab);
  }

  protected templateFor(id: string): TemplateRef<unknown> | null {
    return this.templates().get(id) ?? null;
  }

  protected onTabChange(value: string | number | undefined): void {
    if (value === undefined || value === null) return; // PrimeNG can emit undefined; never leave the page tab-less.
    this.activeTab.set(String(value));
  }

  /** Remembers the outgoing tab's scroll, marks the incoming tab visited, restores its scroll and keeps it in view. */
  private showTab(next: string): void {
    const scroller = this.scroller()?.nativeElement ?? null;
    if (scroller && this.shownTab !== null && this.shownTab !== next) {
      this.scrollPositions.set(this.shownTab, scroller.scrollTop);
    }
    this.shownTab = next;

    if (!this.visited().has(next)) {
      this.visited.update((set) => new Set(set).add(next));
    }

    // After the panel switch has rendered.
    requestAnimationFrame(() => {
      const el = this.scroller()?.nativeElement;
      if (el) el.scrollTop = this.scrollPositions.get(next) ?? 0;
      const activeTabEl = this.host.nativeElement.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]');
      activeTabEl?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });
  }
}
